import React, { useEffect, useMemo, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from '@/utils/router';
import Logo from '@/components/Logo/Logo';
import Footer from '@/components/Footer/Footer';
import ToggleLanguageButton from '@/components/ToggleLanguageButton/ToggleLanguageButton';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import { getPlanningGuideGroups } from '@/constants/event-planning';
import {
  globalTypePath,
  typeLocationPath,
  type EventType,
  type Region,
} from '@/constants/geo';
import { useI18n } from '@/i18n/LanguageProvider';
import {
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
  withBet,
  type SeoService,
} from '@/constants/core';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

type PageDirection = 'rtl' | 'ltr';
type PlanningGuideGroup = ReturnType<typeof getPlanningGuideGroups>[number];
type PlanningGuideItem = PlanningGuideGroup['items'][number];
type PlanningGuideParentLink = { href: string; label: string };

const getGuideCarouselVisibleCount = () => {
  if (typeof window === 'undefined') return 3;

  const isMobile = window.matchMedia(
    `(max-width: ${MEDIA_BREAKPOINTS.mediumScreen})`,
  ).matches;
  if (isMobile) return 1;

  const isTablet = window.matchMedia('(max-width: 980px)').matches;
  return isTablet ? 2 : 3;
};

const PlanningGlobalStyle = createGlobalStyle`
  html {
    scrollbar-color: #b897ff #12131a;
  }

  body,
  #root {
    background: #12131a;
  }

  ::-webkit-scrollbar-track {
    background: #12131a;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #d8c7ff 0%, #8f6dff 100%);
    border: 3px solid #12131a;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #f0e8ff 0%, #b897ff 100%);
  }
`;

const Page = styled.div<{ $dir: PageDirection }>`
  min-height: 100vh;
  overflow-x: hidden;
  direction: ${({ $dir }) => $dir};
  color: var(--planning-ink);
  font-family: ${({ theme }) => theme.fonts.rubikBoldItalic};
  --planning-ink: #ffffff;
  --planning-muted: #e2dcec;
  --planning-soft: #cfc5df;
  --planning-card: rgba(18, 19, 26, 0.36);
  --planning-border: rgba(184, 151, 255, 0.44);
  --planning-border-strong: rgba(216, 199, 255, 0.66);
  --planning-accent: #b897ff;
  --planning-accent-soft: #d8c7ff;
  --planning-accent-deep: #f3ecff;
  background: linear-gradient(135deg, #12131a 0%, #202135 48%, #171b2a 100%);
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(184, 151, 255, 0.22);
  background: rgba(18, 19, 26, 0.74);
  backdrop-filter: blur(14px);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.32);
`;

const HeaderInner = styled.div`
  position: relative;
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 22px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 16px;

  > a {
    grid-column: 2;
    justify-self: center;

    img {
      max-width: min(170px, 48vw);
      height: auto;
      object-fit: contain;
    }
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: minmax(44px, 1fr) auto minmax(44px, 1fr);
    min-height: 50px;
    padding: 8px 12px;
    gap: 8px;

    > a img {
      max-width: min(132px, 34vw);
    }
  }
`;

const LanguageSlot = styled.div`
  grid-column: 3;
  justify-self: end;
  position: relative;
  z-index: 1;
`;

const Main = styled.main`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  max-width: 1120px;
  margin: 0 auto;
  padding: 28px 22px 78px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 16px 14px 52px;
  }
`;

const Hero = styled.section`
  min-width: 0;
  text-align: center;
  max-width: 780px;
  margin: 0 auto 30px;

  h1 {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(2.2rem, 5vw, 4rem);
    line-height: 1.05;
    color: var(--planning-accent-soft);
  }

  p {
    margin: 16px auto 0;
    color: var(--planning-muted);
    font-size: clamp(1rem, 1.8vw, 1.18rem);
    line-height: 1.75;
  }
`;

const Groups = styled.div`
  display: grid;
  min-width: 0;
  gap: 22px;
`;

const GroupHeader = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px 14px;
  flex-wrap: wrap;
  margin: 0 0 4px;

  h2 {
    min-width: 0;
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(1.45rem, 2.5vw, 2rem);
    color: var(--planning-accent-soft);
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    gap: 6px 12px;
  }
`;

const GroupParentLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 32px;
  max-width: 100%;
  padding: 2px 0 3px;
  color: var(--planning-accent-soft);
  text-align: center;
  text-decoration: underline;
  text-decoration-color: var(--planning-accent);
  text-decoration-thickness: 1px;
  text-underline-offset: 5px;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(0.9rem, 1.35vw, 1rem);
  line-height: 1.35;
  background: transparent;
  border: 0;
  box-shadow: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    color: #ffffff;
    text-decoration-color: #ffffff;
    outline: 2px solid rgba(216, 199, 255, 0.34);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    min-height: 30px;
  }
`;

const Group = styled.section`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: clamp(20px, 3vw, 30px) 0;
  border-top: 1px solid rgba(184, 151, 255, 0.2);

  > p {
    margin: 0 0 12px;
    color: var(--planning-muted);
    line-height: 1.62;
  }
`;

const CardsCarousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 18px 58px;
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  background: rgba(18, 19, 26, 0.2);
  backdrop-filter: blur(10px);
  overflow: hidden;
  isolation: isolate;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 14px 52px;
  }
`;

const CarouselControl = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 4px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  box-sizing: border-box;
  transform: translateY(-50%);
  border: 1px solid var(--planning-border-strong);
  border-radius: 999px;
  background: rgba(18, 19, 26, 0.64);
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(8, 7, 13, 0.16);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background: rgba(184, 151, 255, 0.18);
    border-color: rgba(216, 199, 255, 0.82);
    color: #ffffff;
    outline: 2px solid rgba(184, 151, 255, 0.5);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    ${({ $side }) => $side}: 5px;
    width: 40px;
    height: 40px;
  }
`;

const Cards = styled.div<{ $visible: number; $dir: PageDirection }>`
  direction: ${({ $dir }) => $dir};
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  grid-template-columns: ${({ $visible }) =>
    `repeat(${$visible}, minmax(0, 1fr))`};
  align-items: stretch;
  gap: 14px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 7px;
  width: 100%;
  min-width: 0;
  height: 390px;
  min-height: 390px;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  padding: 12px;
  color: var(--planning-ink);
  text-decoration: none;
  background: var(--planning-card);
  border: 1px solid var(--planning-border);
  backdrop-filter: blur(10px);
  box-shadow: 0 16px 34px rgba(8, 7, 13, 0.16);

  &:hover,
  &:focus-visible {
    background: rgba(184, 151, 255, 0.12);
    border-color: var(--planning-border-strong);
    outline: 2px solid rgba(216, 199, 255, 0.32);
    outline-offset: 3px;
  }

  > img {
    width: 100%;
    box-sizing: border-box;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid rgba(216, 199, 255, 0.18);
    box-shadow: 0 8px 18px rgba(8, 7, 13, 0.14);
  }

  > strong {
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    color: var(--planning-accent-soft);
    line-height: 1.28;
    display: -webkit-box;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  > span {
    color: var(--planning-muted);
    line-height: 1.48;
    font-size: 0.94rem;
    display: -webkit-box;
    max-height: calc(1.48em * 6);
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    height: 560px;
    min-height: 560px;
    gap: 8px;

    > span {
      max-height: calc(1.48em * 9);
      -webkit-line-clamp: 9;
    }
  }
`;

const CityVenueVisual = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  box-sizing: border-box;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid rgba(216, 199, 255, 0.26);
  background:
    radial-gradient(
      circle at 50% -12%,
      rgba(216, 199, 255, 0.3) 0%,
      rgba(184, 151, 255, 0.16) 30%,
      transparent 58%
    ),
    linear-gradient(
      90deg,
      transparent 0%,
      rgba(216, 199, 255, 0.12) 18%,
      transparent 34%,
      transparent 66%,
      rgba(216, 199, 255, 0.12) 82%,
      transparent 100%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 34%,
      rgba(8, 7, 13, 0.22) 100%
    ),
    linear-gradient(
      135deg,
      rgba(18, 19, 26, 0.9) 0%,
      rgba(50, 38, 78, 0.6) 52%,
      rgba(18, 19, 26, 0.92) 100%
    );
  box-shadow: 0 8px 18px rgba(8, 7, 13, 0.14);
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 13px;
    border: 1px solid rgba(216, 199, 255, 0.24);
    border-radius: 6px;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset-inline: 18px;
    bottom: 20px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(216, 199, 255, 0.76),
      transparent
    );
    opacity: 0.78;
  }
`;

const CityVenueContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: 7px;
  width: min(86%, 320px);
  padding: 18px 16px 22px;
  text-align: center;
`;

const CityVenueKicker = styled.span`
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(0.82rem, 1.2vw, 0.96rem);
  line-height: 1.2;
`;

const CityVenueName = styled.span`
  max-width: 100%;
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(1.45rem, 2.35vw, 2.05rem);
  line-height: 1.05;
  overflow-wrap: anywhere;
  text-shadow: 0 2px 16px rgba(8, 7, 13, 0.44);
`;

const CityVenueMark = styled.div`
  position: absolute;
  inset-inline: 26px;
  bottom: 18px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  justify-content: center;
  gap: 5px;
  opacity: 0.34;
  z-index: 0;

  span {
    height: 30px;
    border: 1px solid rgba(216, 199, 255, 0.38);
    border-radius: 4px 4px 0 0;
    background: rgba(216, 199, 255, 0.1);
  }

  span:nth-child(2) {
    height: 44px;
  }

  span:nth-child(3) {
    height: 24px;
  }

  span:nth-child(4) {
    height: 52px;
  }
`;

function GuideCardMedia({
  cityLabel,
  cityVisualLabel,
  item,
}: {
  cityLabel?: string;
  cityVisualLabel: string;
  item: PlanningGuideItem;
}) {
  if (cityLabel) {
    return (
      <CityVenueVisual aria-hidden="true">
        <CityVenueMark>
          <span />
          <span />
          <span />
          <span />
          <span />
        </CityVenueMark>
        <CityVenueContent>
          <CityVenueKicker>{cityVisualLabel}</CityVenueKicker>
          <CityVenueName>{cityLabel}</CityVenueName>
        </CityVenueContent>
      </CityVenueVisual>
    );
  }

  return (
    <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" />
  );
}

export default function PlanningGuidePage() {
  const { i18n, lang } = useI18n();
  const direction: PageDirection = lang === 'he' ? 'rtl' : 'ltr';
  const groups = useMemo(() => getPlanningGuideGroups(i18n), [i18n]);
  const [carouselIndexes, setCarouselIndexes] = useState<
    Record<string, number>
  >({});
  const [visibleCarouselCount, setVisibleCarouselCount] = useState(
    getGuideCarouselVisibleCount,
  );
  const parentLinks: Array<{ service: SeoService; type: EventType }> = [
    { service: SEO_SERVICE_WEDDING, type: 'חתונה' },
    { service: SEO_SERVICE_HENNA, type: 'חינה' },
    { service: SEO_SERVICE_BAR_BAT, type: 'בר-מצווה' },
    { service: SEO_SERVICE_EVENT, type: 'אירוע' },
  ];
  const parentLinkByService = new Map(
    parentLinks.map(item => [
      item.service,
      {
        href: globalTypePath(item.type),
        label: i18n.preferredKeywordForService(item.service),
      },
    ]),
  );
  const buildRegionParentLink = (
    region?: Region,
  ): PlanningGuideParentLink | undefined => {
    if (!region) return undefined;

    const label = i18n.preferredKeywordForService(SEO_SERVICE_EVENT);

    return {
      href: typeLocationPath('אירוע', region),
      label:
        i18n.HTML_LANG === 'he'
          ? `${label}${withBet(region)}`
          : `${label} - ${i18n.translateRegion(region)}`,
    };
  };

  useEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  useEffect(() => {
    const updateVisibleCount = () => {
      setVisibleCarouselCount(getGuideCarouselVisibleCount());
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  useEffect(() => {
    setCarouselIndexes(current => {
      let didChange = false;
      const next = { ...current };
      const groupKeys = new Set(groups.map(group => String(group.service)));

      Object.keys(next).forEach(groupKey => {
        if (!groupKeys.has(groupKey)) {
          delete next[groupKey];
          didChange = true;
        }
      });

      groups.forEach(group => {
        const groupKey = String(group.service);
        const visibleCount = Math.max(
          1,
          Math.min(group.items.length, visibleCarouselCount),
        );
        const maxIndex = Math.max(0, group.items.length - visibleCount);
        const currentIndex = next[groupKey] ?? 0;
        const clampedIndex = Math.max(0, Math.min(currentIndex, maxIndex));

        if (currentIndex !== clampedIndex) {
          next[groupKey] = clampedIndex;
          didChange = true;
        }
      });

      return didChange ? next : current;
    });
  }, [groups, visibleCarouselCount]);

  const moveCarousel = (
    groupKey: string,
    maxIndex: number,
    nextIndex: number,
  ) => {
    setCarouselIndexes(current => ({
      ...current,
      [groupKey]: Math.max(0, Math.min(maxIndex, nextIndex)),
    }));
  };

  const nextControlSide = direction === 'rtl' ? 'left' : 'right';
  const previousControlSide = direction === 'rtl' ? 'right' : 'left';
  const nextControlSymbol = direction === 'rtl' ? '←' : '→';
  const previousControlSymbol = direction === 'rtl' ? '→' : '←';

  return (
    <>
      <PlanningGlobalStyle />
      <Page $dir={direction}>
        <Header>
          <HeaderInner>
            <Logo decorative={false} logoTone="light" />
            <LanguageSlot>
              <ToggleLanguageButton compactDesktop tone="light" />
            </LanguageSlot>
          </HeaderInner>
        </Header>

        <Main>
          <Hero>
            <h1>{i18n.PLANNING_GUIDE_TITLE}</h1>
            <p>{i18n.PLANNING_GUIDE_SUBTITLE}</p>
          </Hero>

          <Groups>
            {groups.map((group, groupIndex) => {
              const groupKey = String(group.service);
              const cardsId = `planning-guide-cards-${groupIndex}`;
              const visibleCardCount = Math.max(
                1,
                Math.min(group.items.length, visibleCarouselCount),
              );
              const maxCarouselIndex = Math.max(
                0,
                group.items.length - visibleCardCount,
              );
              const safeCarouselIndex = Math.min(
                carouselIndexes[groupKey] ?? 0,
                maxCarouselIndex,
              );
              const visibleItems = group.items.slice(
                safeCarouselIndex,
                safeCarouselIndex + visibleCardCount,
              );
              const hasCarouselControls =
                group.items.length > visibleCardCount;
              const groupRegion =
                'region' in group ? group.region : undefined;
              const groupParentLink =
                buildRegionParentLink(groupRegion) ??
                parentLinkByService.get(group.service as SeoService);

              return (
                <Group key={group.service}>
                  <GroupHeader>
                    <h2>{group.title}</h2>
                    {groupParentLink ? (
                      <GroupParentLink to={groupParentLink.href}>
                        {groupParentLink.label}
                      </GroupParentLink>
                    ) : null}
                  </GroupHeader>
                  <p>{group.subtitle}</p>
                  <CardsCarousel
                    role="region"
                    aria-label={i18n.PLANNING_CAROUSEL_LABEL(group.title)}
                  >
                    {hasCarouselControls ? (
                      <>
                        <CarouselControl
                          type="button"
                          $side={nextControlSide}
                          onClick={() =>
                            moveCarousel(
                              groupKey,
                              maxCarouselIndex,
                              safeCarouselIndex + 1,
                            )
                          }
                          disabled={safeCarouselIndex >= maxCarouselIndex}
                          aria-controls={cardsId}
                          aria-label={i18n.PLANNING_NEXT_LABEL}
                        >
                          {nextControlSymbol}
                        </CarouselControl>
                        <CarouselControl
                          type="button"
                          $side={previousControlSide}
                          onClick={() =>
                            moveCarousel(
                              groupKey,
                              maxCarouselIndex,
                              safeCarouselIndex - 1,
                            )
                          }
                          disabled={safeCarouselIndex === 0}
                          aria-controls={cardsId}
                          aria-label={i18n.PLANNING_PREVIOUS_LABEL}
                        >
                          {previousControlSymbol}
                        </CarouselControl>
                      </>
                    ) : null}
                    <Cards
                      id={cardsId}
                      $visible={visibleCardCount}
                      $dir={direction}
                      aria-live="polite"
                    >
                      {visibleItems.map(item => {
                        const cityLabel = item.locationCity
                          ? i18n.translateCity(item.locationCity)
                          : undefined;

                        return (
                          <Card key={item.href} to={item.href}>
                            <GuideCardMedia
                              item={item}
                              cityLabel={cityLabel}
                              cityVisualLabel={
                                i18n.PLANNING_CITY_VENUE_GUIDE.visualLabel
                              }
                            />
                            <strong>{item.title}</strong>
                            <span>{item.description}</span>
                          </Card>
                        );
                      })}
                    </Cards>
                  </CardsCarousel>
                </Group>
              );
            })}
          </Groups>
        </Main>

        <Footer
          showPlanningParentLinks
          showFeaturedPlanningGuideLink={false}
          variant="planning"
        />
      </Page>
    </>
  );
}
